// Numeric keys for the report "Строки"/"Подстроки" dimensions, mirroring the
// React app's REPORT_STRING_OPTIONS so both selects share one option set.
export const REPORT_STRING_OPTIONS = {
  offers: 0,
  campaign: 1,
  days: 2,
  channels: 3,
  creatives: 4,
  verticals: 5,
  triggers: 6,
  abonents: 7,
  daysOfWeek: 8,
  weeks: 9,
  month: 10,
  sendTypes: 11,
  trafficProviders: 12,
  landings: 13,
  mailingTypes: 14,
} as const;

// Grouped options for the "Строки" (section) and "Подстроки" (groupByNested)
// selects, mirroring generateReportStringOptions in the React report form.
export const REPORT_STRING_GROUPS = [
  {
    title: { path: "page.report.stringOptions.group.date", default: "Даты" },
    options: [
      {
        key: REPORT_STRING_OPTIONS.days,
        title: { path: "page.report.stringOptions.2", default: "Дни" },
      },
      {
        key: REPORT_STRING_OPTIONS.daysOfWeek,
        title: { path: "page.report.stringOptions.8", default: "Дни недели" },
      },
      {
        key: REPORT_STRING_OPTIONS.weeks,
        title: { path: "page.report.stringOptions.9", default: "Недели" },
      },
      {
        key: REPORT_STRING_OPTIONS.month,
        title: { path: "page.report.stringOptions.10", default: "Месяцы" },
      },
    ],
  },
  {
    title: { path: "page.report.stringOptions.group.param", default: "Параметры" },
    options: [
      {
        key: REPORT_STRING_OPTIONS.offers,
        title: { path: "page.report.stringOptions.0", default: "Предложения" },
      },
      {
        key: REPORT_STRING_OPTIONS.channels,
        title: { path: "page.report.stringOptions.3", default: "Каналы" },
      },
      {
        key: REPORT_STRING_OPTIONS.creatives,
        title: { path: "page.report.stringOptions.4", default: "Креативы" },
      },
      {
        key: REPORT_STRING_OPTIONS.triggers,
        title: { path: "page.report.stringOptions.6", default: "Триггеры" },
      },
      {
        key: REPORT_STRING_OPTIONS.landings,
        title: { path: "page.report.stringOptions.13", default: "Лендинги" },
      },
      {
        key: REPORT_STRING_OPTIONS.mailingTypes,
        title: { path: "page.report.stringOptions.14", default: "Типы отправки" },
      },
    ],
  },
  {
    title: { path: "page.report.stringOptions.group.commun", default: "Коммуникации" },
    options: [
      {
        key: REPORT_STRING_OPTIONS.campaign,
        title: { path: "page.report.stringOptions.1", default: "Кампании" },
      },
    ],
  },
  {
    title: { path: "page.report.stringOptions.group.contractor", default: "Контрагенты" },
    options: [
      {
        key: REPORT_STRING_OPTIONS.trafficProviders,
        title: { path: "page.report.stringOptions.12", default: "Поставщики трафика" },
      },
    ],
  },
] as const;

// Flat key → option lookup over REPORT_STRING_GROUPS, for resolving a label by
// the selected value.
export const REPORT_STRING_OPTION_BY_KEY = Object.fromEntries(
  REPORT_STRING_GROUPS.flatMap((group) => group.options.map((option) => [option.key, option])),
) as Record<number, { key: number; title: { path: string; default: string } }>;

export const GROUPS_BY_OPTIONS = [
  {
    key: null,
    title: {
      path: "page.createReport.groupBy.without.title",
      default: "Без разбивки",
    },
  },
  {
    key: 4,
    title: {
      path: "page.createReport.groupBy.creative.title",
      default: "Креатив",
    },
  },
  {
    key: 0,
    title: {
      path: "page.createReport.groupBy.offer.title",
      default: "Оффер",
    },
  },
  {
    key: 1,
    title: {
      path: "page.createReport.groupBy.campaign.title",
      default: "Кампания",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.days,
    title: {
      path: "page.createReport.groupBy.days.title",
      default: "Дни",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.mailingTypes,
    title: {
      path: "page.createReport.groupBy.mailingTypes.title",
      default: "Тип отправки",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.triggers,
    title: {
      path: "page.createReport.groupBy.trigger.title",
      default: "Триггер",
    },
  },
];

export const SECTION_OPTIONS = [
  {
    key: REPORT_STRING_OPTIONS.days,
    title: { path: "page.createReport.entities.days.title", default: "Дни" },
    description: {
      path: "page.createReport.entities.days.description",
      default: "Сравнение по дням",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.campaign,
    title: { path: "page.createReport.entities.campaign.title", default: "Кампания" },
    description: {
      path: "page.createReport.entities.campaign.description",
      default: "Сравнение по кампаниям",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.offers,
    title: { path: "page.createReport.entities.offer.title", default: "Оффер" },
    description: {
      path: "page.createReport.entities.offer.description",
      default: "Сравнение по офферам",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.mailingTypes,
    title: {
      path: "page.createReport.entities.mailingTypes.title",
      default: "Тип отправки",
    },
    description: {
      path: "page.createReport.entities.mailingTypes.description",
      default: "Сравнение по типам отправки",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.creatives,
    title: { path: "page.createReport.entities.creative.title", default: "Креатив" },
    description: {
      path: "page.createReport.entities.creative.description",
      default: "Сравнение по креативам",
    },
  },
  {
    key: REPORT_STRING_OPTIONS.triggers,
    title: { path: "page.createReport.entities.trigger.title", default: "Триггер" },
    description: {
      path: "page.createReport.entities.trigger.description",
      default: "Сравнение по триггерам",
    },
  },
];

// Each indicator declares how its total is computed across rows — mirrors the
// React COLUMNS mapping in `apps/afina-react/src/shared/constants/statistics.ts`
// so the period-comparison numbers match the backend's other consumer:
//   - "sum": sum of parsed values
//   - "mean": arithmetic mean of parsed values
//   - "arpc": sum(approvedSum) / sum(sends)
export const INDICATOR_OPTIONS = [
  {
    key: 0,
    name: "sends",
    title: { path: "indicators.0", default: "Отправлено" },
    format: "number",
    calculation: "sum",
  },
  {
    key: 1,
    name: "delivers",
    title: { path: "indicators.1", default: "Доставлено" },
    format: "number",
    calculation: "sum",
  },
  {
    key: 2,
    name: "views",
    title: { path: "indicators.2", default: "Просмотрено" },
    format: "number",
    calculation: "sum",
  },
  {
    key: 3,
    name: "expenses",
    title: { path: "indicators.3", default: "Расходы" },
    format: "money",
    calculation: "sum",
  },
  {
    key: 4,
    name: "clicks",
    title: { path: "indicators.4", default: "Клики" },
    format: "number",
    calculation: "sum",
  },
  {
    key: 5,
    name: "ctr",
    title: { path: "indicators.5", default: "CTR" },
    format: "percent",
    calculation: "mean",
  },
  {
    key: 6,
    name: "approves",
    title: { path: "indicators.6", default: "Approves" },
    calculation: "sum",
  },
  {
    key: 7,
    name: "holds",
    title: { path: "indicators.7", default: "Holds" },
    calculation: "sum",
  },
  {
    key: 8,
    name: "rejects",
    title: { path: "indicators.8", default: "Rejects" },
    calculation: "sum",
  },
  {
    key: 9,
    name: "approvedSum",
    title: { path: "indicators.9", default: "Approved SUM" },
    calculation: "sum",
  },
  {
    key: 10,
    name: "holdSum",
    title: { path: "indicators.10", default: "Hold SUM" },
    calculation: "sum",
  },
  {
    key: 11,
    name: "rejectedSum",
    title: { path: "indicators.11", default: "Rejected SUM" },
    calculation: "sum",
  },
  {
    key: 12,
    name: "actions",
    title: { path: "indicators.12", default: "Действия" },
    calculation: "sum",
  },
  {
    key: 13,
    name: "cr",
    title: { path: "indicators.13", default: "CR" },
    format: "percent",
    calculation: "mean",
  },
  {
    key: 14,
    name: "ar",
    title: { path: "indicators.14", default: "AR" },
    format: "percent",
    calculation: "mean",
  },
  {
    key: 15,
    name: "hr",
    title: { path: "indicators.15", default: "HR" },
    format: "percent",
    calculation: "mean",
  },
  {
    key: 16,
    name: "rr",
    title: { path: "indicators.16", default: "RR" },
    format: "percent",
    calculation: "mean",
  },
  {
    key: 17,
    name: "profit",
    title: { path: "indicators.17", default: "Profit" },
    format: "money",
    calculation: "sum",
  },
  {
    key: 18,
    name: "lpClicks",
    title: { path: "indicators.18", default: "LP Clicks" },
    calculation: "sum",
  },
  {
    key: 19,
    name: "overallCTR",
    title: { path: "indicators.19", default: "Overall CTR" },
    format: "percent",
    calculation: "mean",
  },
  {
    key: 20,
    name: "subsReachRate",
    title: { path: "indicators.20", default: "Subs Reach Rate" },
    calculation: "mean",
  },
  {
    key: 21,
    name: "triggerActivations",
    title: { path: "indicators.21", default: "Trigger Activations" },
    calculation: "sum",
  },
  {
    key: 22,
    name: "earnPerClick",
    title: { path: "indicators.22", default: "Earn Per Click" },
    calculation: "mean",
  },
  {
    key: 23,
    name: "subsActivationRate",
    title: { path: "indicators.23", default: "Subs Activation Rate" },
    calculation: "sum",
  },
  {
    key: 24,
    name: "subsReach",
    title: { path: "indicators.24", default: "Subs Reach" },
    calculation: "sum",
  },
  {
    key: 25,
    name: "subsActivation",
    title: { path: "indicators.25", default: "Subs Activation" },
    calculation: "sum",
  },
  {
    key: 26,
    name: "totalSubs",
    title: { path: "indicators.26", default: "Total Subs" },
    calculation: "sum",
  },
  {
    key: 27,
    name: "arpc",
    title: { path: "indicators.27", default: "ARPC" },
    format: "money",
    calculation: "sum",
  },
  {
    key: 28,
    name: "triggerUtilizationRate",
    title: { path: "indicators.28", default: "Trigger Utilization Rate" },
    calculation: "mean",
  },
] as const;

export const INDICATORS_BY_KEY = Object.fromEntries(
  INDICATOR_OPTIONS.map((option) => [option.key, option]),
) as Record<number, (typeof INDICATOR_OPTIONS)[number]>;

export const CREATE_REPORT_INDICATOR_KEYS = [0, 1, 2, 3, 4, 5, 13, 14, 17, 27] as const;

export const CREATE_REPORT_INDICATORS = CREATE_REPORT_INDICATOR_KEYS.map(
  (key) => INDICATORS_BY_KEY[key],
);

export const ATTRIBUTION_OPTIONS = [
  {
    key: 1,
    title: { path: "page.createReport.attribution.stream.title", default: "По потоку" },
    subtitle: { path: "page.createReport.attribution.stream.subtitle", default: "дата конверсии" },
    description: {
      path: "page.createReport.attribution.stream.description",
      default: "События группируются по дате прихода конверсии.",
    },
  },
  {
    key: 0,
    title: { path: "page.createReport.attribution.funnel.title", default: "По воронке" },
    subtitle: { path: "page.createReport.attribution.funnel.subtitle", default: "дата отправки" },
    description: {
      path: "page.createReport.attribution.funnel.description",
      default: "События группируются по дате отправки сообщения.",
    },
  },
];

export const PERIOD_OPTIONS = [
  { key: 5, label: { path: "page.report.period.today", default: "Сегодня" } },
  { key: 11, label: { path: "page.report.period.yesterday", default: "Вчера" } },
  { key: 0, label: { path: "page.report.period.previousDay", default: "Последние сутки" } },
  { key: 7, label: { path: "page.report.period.currentMonth", default: "Текущий месяц" } },
  { key: 10, label: { path: "page.report.period.3days", default: "Последние 3 дня" } },
  { key: 1, label: { path: "page.report.period.7days", default: "7 дней" } },
  { key: 2, label: { path: "page.report.period.30days", default: "30 дней" } },
  { key: 4, label: { path: "page.report.period.365days", default: "365 дней" } },
  { key: "fixPeriod", label: { path: "page.report.period.custom", default: "Свой период" } },
];
